/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import React, { useEffect, useState } from "react";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { notSelect } from "../styles/general";
import { Option } from "../types/SavedSearchTypes";
import StyledSelect from "./StyledSelect";

export function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (typeof a[orderBy] === "string" || typeof b[orderBy] === "string") {
    if (!b[orderBy] || !a[orderBy]) return 0;

    if ((b[orderBy] as any).toLowerCase() < (a[orderBy] as any).toLowerCase()) {
      return -1;
    }
    if ((b[orderBy] as any).toLowerCase() > (a[orderBy] as any).toLowerCase()) {
      return 1;
    }
    return 0;
  } else {
    if (b[orderBy] < a[orderBy]) {
      return -1;
    }
    if (b[orderBy] > a[orderBy]) {
      return 1;
    }
    return 0;
  }
}

export function stableSort<T>(array: T[], comparator: (a: T, b: T) => any) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, any]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

export function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key
): (a: { [key in Key]: any }, b: { [key in Key]: any }) => number {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

export function showPages(rowsPerPage: number, page: number, length: number) {
  const secondNumber =
    (page + 1) * rowsPerPage > length ? length : (page + 1) * rowsPerPage;

  const firstNumber = rowsPerPage * page + 1;

  return { firstNumber, secondNumber };
}

const ContainerFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  height: 60px;
  border: 1px solid #eaeaeabf;
  background-color: #ffffff;
  align-items: center;
  padding: 15px;
  color: #2f2f2f;
  ${notSelect}
  width: 100%;
`;

const marginTop15 = css`
  margin-top: 15px;
`;

const marginLeftRight15 = css`
  margin-left: 15px;
  margin-right: 15px;
`;

const ButtonIcon = styled.img`
  width: 12px;
  height: 12px;
  cursor: pointer;
`;

const IconContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  cursor: pointer;

  &:hover {
    background-color: rgba(0, 0, 0, 0.04);
  }
`;

export type Order = "asc" | "desc";

const ROWS_PER_PAGE: Option[] = [
  {
    id: "25",
    name: "25",
  },
  {
    id: "50",
    name: "50",
  },
  {
    id: "100",
    name: "100",
  },
];

const MIN_ROWS_PER_PAGE: Option[] = [
  {
    id: "5",
    name: "5",
  },
  {
    id: "10",
    name: "10",
  },
  {
    id: "20",
    name: "20",
  },
];
interface Props {
  page: number;
  length: number;
  rowsPerPage: number;
  handlePage: (vle: number) => void;
  setRowsPerPage: (vlw: number) => void;
  filterOptions?: any;
  isSmaller?: boolean;
  handleContactsPerPage?: (vle: number) => void;
}

export function TableFooter(props: Props): JSX.Element {
  const {
    page,
    length,
    rowsPerPage,
    handlePage,
    setRowsPerPage,
    filterOptions,
    isSmaller,
    handleContactsPerPage,
  } = props;
  const [numberPages, setNumberPages] = useState({
    firstNumber: 0,
    secondNumber: length > 0 ? 1 : 0,
  });

  const nextPage = () => {
    if (page + 1 < Math.ceil(length / rowsPerPage))
      handlePage(length > 0 ? page + 1 : 0);
  };

  const previousPage = () => {
    if (page > 0) handlePage(page - 1);
  };

  useEffect(() => {
    const showPagesNumbers = showPages(rowsPerPage, page, length);
    setNumberPages(showPagesNumbers);

    handleContactsPerPage &&
      handleContactsPerPage(showPagesNumbers.secondNumber);
  }, [page, rowsPerPage, length, filterOptions]);

  return (
    <ContainerFooter css={marginTop15}>
      <div
        css={css`
          margin-right: 30px;
          display: flex;
          flex-direction: row;
          align-items: center;
          justify-content: center;
        `}
      >
        <span
          css={css`
            margin-right: 10px;
          `}
        >
          Rows per page:
        </span>
        <StyledSelect
          customCss={css`
            width: 70px;
          `}
          above
          options={isSmaller ? MIN_ROWS_PER_PAGE : ROWS_PER_PAGE}
          selectedOption={(isSmaller ? MIN_ROWS_PER_PAGE : ROWS_PER_PAGE).find(
            (o) => o.id === rowsPerPage.toString()
          )}
          handleSelectedOption={(e) => {
            setRowsPerPage(parseInt(e.id));
            handlePage(length / parseInt(e.id) < 1 && 0);
          }}
        />
      </div>
      <div>
        <span>
          {length === 0 ? 0 : numberPages.firstNumber} -{" "}
          {numberPages.secondNumber} of {length}
        </span>
      </div>
      <IconContainer onClick={() => previousPage()} css={marginLeftRight15}>
        <FaChevronLeft color="#bbbbbb" />
      </IconContainer>
      <IconContainer onClick={() => nextPage()}>
        <FaChevronRight color="#bbbbbb" />
      </IconContainer>
    </ContainerFooter>
  );
}
